/**
 * A built workspace, read back as a studio draft (step 9.13 of
 * docs/plans/onboarding-harness/phase-09.md): the workspace as it stands, in
 * the shape the studio draws and the same ops change. Edit the setup (9.16)
 * reads it in `edit` mode; Start like (9.14) in `copy` mode.
 *
 * Keys are minted from names (`mintStudioKey`): lower snake case, at most 40
 * characters, `_2`, `_3` for a name taken in the same scope. Records keep the
 * catalogue's order, fields their form order (then id), cards their place,
 * links their order (then id), roles the order given; so the same workspace
 * always gives the same keys.
 *
 * What a draft cannot say is not guessed at. It is left out of the draft and
 * named in `kept`, with why:
 *
 *   record   in `copy` mode, a record or list with no field that can name a
 *            row (a new setup names every row by a headline); with no title
 *            bound, the first field that can is its headline. In `edit` mode
 *            such a record is drafted all the same, `headless` with no
 *            headline (`StudioRecord.headless`): it stands in the workspace
 *            and is drawn and edited like any other
 *   formula  a worked-out field whose recipe does not read back, a rollup
 *   lines    a field holding lines (its lines record is a draft of its own)
 *   link     a link with a condition through a pick (`via`), on a field the
 *            draft lacks, on a list's rows, a pattern or landing that is not a
 *            feed's new row; a pick from a list the draft does not hold
 *   card     a record's card on top whose total or name does not read back
 *   role     a role whose name the draft cannot take (Admin, Member, Viewer)
 *   money    in `copy` mode, a list's money field, or a money formula there
 *            that reads more than the totals and formulas of its row: the
 *            judge refuses one (`DIRECTORY_HOLDS_MONEY`, typed money lives on
 *            entries, W11 of docs/plans/workbook-map.md), and a copy
 *            carries only what the judge allows (Q8 of
 *            docs/plans/qa-fixes-1.md). A recipe or default that reads it
 *            goes with it, as for any field left out
 *
 * Never copied: a list's rows ("No entries, customers or staff are copied",
 * board 9.13) and a role's people (decision 4: invites come after Build). The
 * two roles every workspace is made with (Admin, Member) are not drafted, and
 * neither is the primary admin's. A role's per-record access is per person in
 * RecordOS (it rides on each invite, 8.6), so a drafted role carries none.
 *
 * `built` (edit mode only) maps every part path, kept parts included, to the
 * id it stands for: a record's, a field's, a role's, and for a card
 * `<summary view id>#<place>`. In copy mode it is empty.
 *
 * `summarise` is kept for its callers: it summarised links, which no
 * workspace holds any more (step 9.5 of docs/plans/ledger.md).
 *
 * Pure: no clock, no I/O.
 */
import type { AgentLanguageTag } from './agent-language';
import { DEFAULT_AGENT_LANGUAGE } from './agent-language';
import { isRecordIconId, type RecordSchemaField, type RecordSchemaResponse, type VisibleRecordKind } from './api';
import { PURPOSES } from './generated/purposes';
import {
  AUTHOR_TYPES,
  SETUP_AGGS,
  SETUP_TILE_OPS,
  SETUP_TILE_SCOPES,
  type AuthorType,
  type SetupDirection,
  type SetupDraftField,
  type SetupDraftFormula,
  type SetupDraftMustBe,
  type SetupDraftFormulaNode,
  type SetupDraftTileCondition,
} from './compose-author';
import { formulaOf, readFieldSpec, readsOnlyWorkedOut, type FormulaNode, type FormulaSpec, type MustBeRule } from './formula';
import { tOr } from './i18n/t';
import { isInsightCardId } from './insights';
import type { WorkspaceRole } from './people';
import { allPermissions, memberPermissions, PERMISSION_KEYS, viewerPermissions, type RolePermissions } from './roles';
import {
  emptyStudioDraft,
  STUDIO_LIMITS,
  type StudioDraft,
  type StudioRecord,
  type StudioRole,
  type StudioTile,
} from './setup-draft/draft';
import type { StudioOp } from './setup-draft/ops';
import { mintStudioKey, studioPath } from './setup-draft/paths';
import { specRuleOf, type SpecSeedAccount } from './spec/posting';
import type { LedgerAccountType } from './ledger/patterns';

/**
 * The reason version 1 of a session started like a workspace held carries
 * (9.14): a catalogue key, so each client says "Started like {{name}}" in its
 * own words.
 */
export const STARTED_LIKE_REASON = 'setup.studio.second.started';

/**
 * The batch that writes a copied draft onto an empty one (9.14): the name and
 * currency, every record and list with its fields and cards, then the links
 * and roles. Nothing else a copy holds.
 */
export const studioCopyOps = (draft: StudioDraft): StudioOp[] => [
  { op: 'set_workspace', ...(draft.meta.name.trim() === '' ? {} : { name: draft.meta.name }), currency: draft.meta.currency },
  ...draft.records.map((record): StudioOp => ({ op: 'add_record', ...structuredClone(record) })),
  ...draft.roles.map((role): StudioOp => ({ op: 'add_role', role: structuredClone(role) })),
];

/* ------------------------------------------------------------------ shapes */

/** One record or list of the workspace: its schema as the owner reads it, its kind, how many entries it holds. */
export type BuiltRecordInput = { readonly schema: RecordSchemaResponse; readonly kind: VisibleRecordKind; readonly entries: number };

export type BuiltWorkspaceInput = {
  readonly name: string;
  /** ISO 4217. */
  readonly currency: string;
  /** The language the draft is talked in; the default when absent. */
  readonly language?: AgentLanguageTag;
  /** In the catalogue's own order (created, then id). Lines records are not here. */
  readonly records: readonly BuiltRecordInput[];
  /** Each list's first names, by list id. Read for the canvas; never copied into the draft. */
  readonly names: ReadonlyMap<string, readonly string[]>;
  readonly roles: readonly WorkspaceRole[];
  /** How the workspace keeps its money (L15, L4): each record's money rule and the Accounts list. Absent: none read. */
  readonly money?: BuiltMoneyInput;
};

/** One row of the Accounts list: its account's id, the row's name, and what the ledger holds of it. */
export type BuiltAccountRow = {
  readonly accountId: string;
  readonly name: string;
  readonly type: LedgerAccountType;
} & SpecSeedAccount;

/**
 * The workspace's money as Build wrote it (step 12.1 of docs/plans/ledger.md):
 * each record's live money rule as `app.posting_rule` stores it, by record id,
 * and the Accounts list with its Type field, each Type option's type, and its
 * rows. Edit the setup and Start like keep both, so a rule is never lost to
 * an edit and a copy counts its money the same way.
 */
export type BuiltMoneyInput = {
  readonly rules: ReadonlyMap<string, { readonly pattern: string; readonly map: unknown }>;
  readonly accounts: {
    readonly recordId: string;
    readonly typeFieldId: string;
    /** The Type field's option ids, each to the type it names. */
    readonly types: Readonly<Record<string, LedgerAccountType>>;
    readonly rows: readonly BuiltAccountRow[];
  } | null;
  /**
   * Each list marked as stock items (Phase 9, D2), by record id: the field ids
   * `app.record.prefs.stock_items` keeps. A record's quantity rule picks from
   * one, so Edit and Start like read the mark back with the rule. Absent: none.
   */
  readonly stockItems?: ReadonlyMap<string, BuiltStockItems>;
};

/** A list's stock items mark as the workspace keeps it: its unit field, and its pack fields. */
export type BuiltStockItems = { readonly unitFieldId: string; readonly packUnitFieldId: string | null; readonly packSizeFieldId: string | null };

export const BUILT_KEPT_WHY = ['record', 'formula', 'lines', 'link', 'card', 'role', 'money'] as const;

/** A part of the workspace the draft does not hold, by the part path it would have had. */
export type BuiltKept = { key: string; name: string; why: (typeof BUILT_KEPT_WHY)[number] };

export type BuiltDraft = {
  draft: StudioDraft;
  built: Readonly<Record<string, string>>;
  kept: readonly BuiltKept[];
};

/**
 * One record's or list's live facts on the Business map: how many live
 * entries the caller's row security lets them count, and when the last was
 * written (ISO 8601; null for none). The catalogue's `entryCount` and
 * `lastEntryAt`, the same facts the map's cards said before it drew the draft.
 */
export type MapDraftFact = { readonly entries: number; readonly lastEntryAt: string | null };

/**
 * `GET /v1/businesses/:id/map-layout`'s sibling, `GET /v1/businesses/:id/map-draft`
 * (step 1.1 of docs/plans/business-map-canvas.md): the workspace as the caller
 * may see it, read back in `edit` mode, and each record's and list's facts by
 * id. No words of the app's own: a client says them.
 */
export type MapDraftResponse = BuiltDraft & { readonly facts: Readonly<Record<string, MapDraftFact>> };

/* ------------------------------------------------------------------- keys */

const mintAll = <T>(items: readonly T[], words: (item: T) => string, fallback: Parameters<typeof mintStudioKey>[2]): string[] => {
  const taken = new Set<string>();
  return items.map((item) => {
    const key = mintStudioKey(words(item), taken, fallback);
    taken.add(key);
    return key;
  });
};

const byOrdThenId = (a: RecordSchemaField, b: RecordSchemaField): number => a.ord - b.ord || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0);

const optionWord = (option: NonNullable<RecordSchemaField['options']>[number]): string => option.label ?? tOr(option.labelKey, option.labelKey);

// A rollup stays out of a draft read back from a workspace, as before Phase 4
// (and a number series, whose `per` id is not read back to a key either):
// reading its spec back into draft keys is not built yet, so it is kept, and so
// is every formula that reads it.
const AUTHORED: ReadonlySet<string> = new Set(AUTHOR_TYPES.filter((type) => type !== 'rollup' && type !== 'series'));

/** The types a field that names a row may be (the title purpose's). */
const TITLE_TYPES: ReadonlySet<string> = new Set(PURPOSES.title.answered_by);

/** The ids of one record's fields and choices, and the keys they become. */
type RecordKeys = {
  readonly recordKey: string;
  readonly schema: RecordSchemaResponse;
  readonly kind: VisibleRecordKind;
  readonly fields: readonly RecordSchemaField[];
  readonly fieldKey: ReadonlyMap<string, string>;
  readonly optionKey: ReadonlyMap<string, ReadonlyMap<string, string>>;
};

/* ---------------------------------------------------------------- formulas */

/**
 * A stored recipe (field ids) in draft words (field keys), or null when one
 * of its ids is not a field the draft holds: a `field` on this record, a
 * `hop` through a pick of this record to a field of its list, an `agg` over a
 * multi_select of this record, of a field of its list.
 *
 * A compare against a choice ("Category is Lab") holds the option id the
 * build minted (`rewriteChoices` in spec-apply.ts); the draft says the
 * option's key, which the next build maps to its own id. A const that names
 * no option of the field it is compared with is left as it is.
 */
const keyedNode = (
  node: FormulaNode,
  here: RecordKeys,
  keyOf: (recordId: string, fieldId: string) => string | null,
  byRecordId: ReadonlyMap<string, RecordKeys>,
): SetupDraftFormulaNode | null => {
  const own = (fieldId: string): string | null => keyOf(here.schema.recordId, fieldId);
  const targetOf = (fieldId: string): string | null => here.fields.find((field) => field.id === fieldId)?.targetRecordId ?? null;
  const go = (child: FormulaNode): SetupDraftFormulaNode | null => keyedNode(child, here, keyOf, byRecordId);
  const choicesAt = (side: FormulaNode): ReadonlyMap<string, string> | undefined => {
    if (side.kind === 'field') return here.optionKey.get(side.fieldId);
    if (side.kind !== 'hop') return undefined;
    const target = targetOf(side.via);
    return target === null ? undefined : byRecordId.get(target)?.optionKey.get(side.fieldId);
  };
  const choiceKeyed = (side: SetupDraftFormulaNode | null, choices: ReadonlyMap<string, string> | undefined): SetupDraftFormulaNode | null => {
    if (side === null || choices === undefined || side.kind !== 'const' || typeof side.value !== 'string') return side;
    return { kind: 'const', value: choices.get(side.value) ?? side.value };
  };
  switch (node.kind) {
    case 'field': {
      const key = own(node.fieldId);
      return key === null ? null : { kind: 'field', fieldKey: key };
    }
    case 'hop': {
      const via = own(node.via);
      const target = targetOf(node.via);
      const key = target === null ? null : keyOf(target, node.fieldId);
      return via === null || key === null ? null : { kind: 'hop', via, fieldKey: key };
    }
    case 'const':
      return { kind: 'const', value: node.value };
    case 'calc': {
      const left = go(node.left);
      const right = go(node.right);
      return left === null || right === null ? null : { kind: 'calc', op: node.op, left, right };
    }
    case 'round': {
      const of = go(node.of);
      return of === null ? null : { kind: 'round', of, decimals: node.decimals };
    }
    case 'if': {
      // Either side may be the choice: "Category is Lab" or "Lab is Category".
      const left = node.right === undefined ? go(node.left) : choiceKeyed(go(node.left), choicesAt(node.right));
      const right = node.right === undefined ? undefined : choiceKeyed(go(node.right), choicesAt(node.left));
      const then = go(node.then);
      const otherwise = go(node.otherwise);
      if (left === null || right === null || then === null || otherwise === null) return null;
      return { kind: 'if', left, op: node.op, ...(right === undefined ? {} : { right }), then, otherwise };
    }
    case 'days_between': {
      const from = go(node.from);
      const to = go(node.to);
      return from === null || to === null ? null : { kind: 'days_between', from, to };
    }
    case 'concat': {
      const parts = node.parts.map(go);
      return parts.some((one) => one === null) ? null : { kind: 'concat', parts: parts as SetupDraftFormulaNode[] };
    }
    case 'coalesce': {
      const of = node.of.map(go);
      return of.some((one) => one === null) ? null : { kind: 'coalesce', of: of as SetupDraftFormulaNode[] };
    }
    case 'agg': {
      const lines = own(node.lines);
      const multi = here.fields.find((field) => field.id === node.lines);
      if (lines === null || multi?.type !== 'multi_select' || multi.targetRecordId === null) return null;
      if (node.of === undefined) return { kind: 'agg', op: node.op, lines };
      const of = keyOf(multi.targetRecordId, node.of);
      return of === null ? null : { kind: 'agg', op: node.op, lines, of };
    }
    case 'date_add': {
      // A unit read from a select holds the choice's id when it runs, and the
      // draft names the select by its key like any other read.
      const date = go(node.date);
      const amount = go(node.amount);
      const unit = go(node.unit);
      if (date === null || amount === null || unit === null) return null;
      return { kind: 'date_add', date, amount, unit, ...(node.validTill ? { validTill: true } : {}) };
    }
    case 'today':
      return { kind: 'today' };
  }
};

const keyedFormula = (
  spec: FormulaSpec,
  here: RecordKeys,
  keyOf: (recordId: string, fieldId: string) => string | null,
  byRecordId: ReadonlyMap<string, RecordKeys>,
): SetupDraftFormula | null => {
  const expr = keyedNode(spec.expr, here, keyOf, byRecordId);
  return expr === null ? null : { result: spec.result, expr, onMissing: spec.onMissing };
};

/** Must be rules by draft keys (F13); a rule whose tree does not read back is left off, the rest kept. */
const keyedRules = (
  rules: readonly MustBeRule[],
  here: RecordKeys,
  keyOf: (recordId: string, fieldId: string) => string | null,
  byRecordId: ReadonlyMap<string, RecordKeys>,
): SetupDraftMustBe[] =>
  rules.flatMap((rule) => {
    const formula = keyedFormula(rule.formula, here, keyOf, byRecordId);
    return formula === null ? [] : [{ formula, message: rule.message, level: rule.level }];
  });

/* ----------------------------------------------------------------- the read */

/** Which way a record moves money, off its measure and sign: the rule `directionFrom` (setup-edit.ts) keeps, with no measure read as none. */
const directionOf = (schema: RecordSchemaResponse, kind: VisibleRecordKind): SetupDirection => {
  if (kind === 'directory' || schema.bindings.measure === undefined) return 'none';
  const sign = schema.bindings.sign;
  if (sign === undefined) return 'count';
  if ('constNum' in sign) return sign.constNum === 1 ? 'in' : 'out';
  return 'both';
};

const boundId = (schema: RecordSchemaResponse, purpose: 'title' | 'time_axis' | 'measure' | 'sign' | 'focused_input'): string | null => {
  const answer = schema.bindings[purpose];
  return answer !== undefined && 'fieldId' in answer ? answer.fieldId : null;
};

const PRESETS: ReadonlyArray<{ base: StudioRole['base']; permissions: () => RolePermissions }> = [
  { base: 'member', permissions: memberPermissions },
  { base: 'viewer', permissions: viewerPermissions },
  { base: 'admin', permissions: allPermissions },
];

/** The names every workspace's roles are made with, which a drafted role never takes. */
const MADE_WITH: ReadonlySet<string> = new Set(['admin', 'member', 'viewer'].map((key) => tOr(`authority.${key}`, key).toLowerCase()));

/**
 * A role as the draft says it: the preset it differs least from, then each
 * permission that differs, in the catalogue's order.
 */
const roleShapeOf = (role: WorkspaceRole): Pick<StudioRole, 'base' | 'permissions'> => {
  const held = role.permissions as Record<string, unknown>;
  const diffs = PRESETS.map(({ base, permissions }) => {
    const preset = permissions() as Record<string, unknown>;
    return { base, pairs: PERMISSION_KEYS.filter((key) => held[key] !== undefined && held[key] !== preset[key]).map((key) => ({ key, value: held[key] as StudioRole['permissions'][number]['value'] })) };
  });
  const best = diffs.reduce((a, b) => (b.pairs.length < a.pairs.length ? b : a));
  return { base: best.base, permissions: best.pairs };
};

/** A tile's name: the word the build wrote on it, else what its key says. */
const tileName = (tile: Record<string, unknown>): string => {
  if (typeof tile.label === 'string' && tile.label.trim() !== '') return tile.label.trim();
  return typeof tile.label_key === 'string' ? tOr(tile.label_key, '') : '';
};

const TILE_EXPR = /^([a-z]+):([^\s:]+)$/;

/**
 * A money formula on a list that reads more than the totals and formulas of
 * its row: the judge refuses it (W11 of docs/plans/workbook-map.md), so a
 * copy leaves it out as it leaves out a typed amount.
 */
const listMoneyNotWorkedOut = (
  field: { readonly type: string; readonly spec?: unknown },
  fields: readonly { readonly id: string; readonly type: string; readonly spec?: unknown }[],
): boolean => {
  if (field.type !== 'formula') return false;
  const formula = formulaOf(readFieldSpec(field.spec));
  if (formula === null || formula.result !== 'money') return false;
  return !readsOnlyWorkedOut(formula, (id) => {
    const one = fields.find((held) => held.id === id);
    return one === undefined ? null : { type: one.type, formula: one.type === 'formula' ? formulaOf(readFieldSpec(one.spec)) : null };
  });
};

/**
 * The workspace as a studio draft. `edit` keeps every part's id in `built`;
 * `copy` keeps none, and either way the draft holds no rows and no people.
 */
export const draftFromWorkspace = (input: BuiltWorkspaceInput, mode: 'edit' | 'copy', _opts: { readonly summarise?: boolean } = {}): BuiltDraft => {
  const kept: BuiltKept[] = [];
  const built: Record<string, string> = {};
  const note = (path: string, id: string): void => {
    if (mode === 'edit') built[path] = id;
  };

  const accounts = input.money?.accounts ?? null;

  /* keys, minted before anything is read, so a part's key never hangs on what else was kept */
  const recordKeys = mintAll(input.records, (record) => record.schema.name, 'r');
  const every: RecordKeys[] = input.records.map((record, at) => {
    const fields = [...record.schema.fields].sort(byOrdThenId);
    const fieldKeys = mintAll(fields, (field) => field.label, 'f');
    return {
      recordKey: recordKeys[at]!,
      schema: record.schema,
      kind: record.kind,
      fields,
      fieldKey: new Map(fields.map((field, index) => [field.id, fieldKeys[index]!])),
      optionKey: new Map(
        fields.map((field) => {
          const options = field.options ?? [];
          // The Accounts list's Type keeps the five types as its option keys, as the kit made them (L15): spec apply reads them so.
          const types = accounts?.recordId === record.schema.recordId && accounts.typeFieldId === field.id ? accounts.types : null;
          const keys = types === null ? mintAll(options, optionWord, 'x') : options.map((option) => types[option.id] ?? mintStudioKey(optionWord(option), new Set(), 'x'));
          return [field.id, new Map(options.map((option, index) => [option.id, keys[index]!]))];
        }),
      ),
    };
  });

  /*
   * A record or list the draft can name a row of. A new setup (`copy`) names
   * each row by a headline, so one with no field that can name a row (a blank
   * record of a date and an amount) is kept, and so, until nothing moves, is
   * one whose only such field picks from a list that was. Edit (`edit`)
   * changes the workspace as it stands, where such a record stands unnamed:
   * it is drafted, headless, so the map draws it and Edit can change it.
   */
  let records = every;
  for (let moved = mode === 'copy'; moved; ) {
    const here = new Set(records.map((record) => record.schema.recordId));
    const nameable = records.filter((record) =>
      record.fields.some((field) => TITLE_TYPES.has(field.type) && (field.type !== 'reference' || (field.targetRecordId !== null && here.has(field.targetRecordId)))),
    );
    moved = nameable.length !== records.length;
    records = nameable;
  }
  const byRecordId = new Map(records.map((record) => [record.schema.recordId, record]));
  for (const record of every) {
    if (byRecordId.has(record.schema.recordId)) continue;
    const path = studioPath({ kind: 'record', record: record.recordKey });
    note(path, record.schema.recordId);
    kept.push({ key: path, name: record.schema.name, why: 'record' });
  }

  /*
   * Which fields the draft cannot hold. A field out for a reason of its own
   * first (lines, a type no draft takes, a pick from a list not here); then,
   * until nothing moves, a recipe reading a field that is out.
   */
  const out = new Map<string, BuiltKept['why']>();
  for (const record of records) {
    for (const field of record.fields) {
      if (field.type === 'lines') out.set(field.id, 'lines');
      // A copy is a new setup the judge reads whole: a list holding money is one it refuses (DIRECTORY_HOLDS_MONEY).
      // A list may show money it works out from its totals (W11 of docs/plans/workbook-map.md), never typed money nor money from a typed number.
      else if (mode === 'copy' && record.kind === 'directory' && (field.type === 'money' || listMoneyNotWorkedOut(field, record.fields))) out.set(field.id, 'money');
      else if (!AUTHORED.has(field.type)) out.set(field.id, 'formula');
      else if ((field.type === 'reference' || field.type === 'multi_select') && (field.targetRecordId === null || !byRecordId.has(field.targetRecordId))) out.set(field.id, 'link');
    }
  }
  const keyOf = (recordId: string, fieldId: string): string | null => {
    if (out.has(fieldId)) return null;
    const record = byRecordId.get(recordId);
    if (record === undefined || !record.fields.some((field) => field.id === fieldId)) return null;
    return record.fieldKey.get(fieldId) ?? null;
  };
  for (let moved = true; moved; ) {
    moved = false;
    for (const record of records) {
      for (const field of record.fields) {
        if (out.has(field.id) || field.type !== 'formula') continue;
        const formula = formulaOf(field.spec);
        if (formula === null || keyedFormula(formula, record, keyOf, byRecordId) === null) {
          out.set(field.id, 'formula');
          moved = true;
        }
      }
    }
  }

  /* records and lists */
  const draftRecords: StudioRecord[] = records.map((record): StudioRecord => {
    const { schema, recordKey } = record;
    note(studioPath({ kind: 'record', record: recordKey }), schema.recordId);
    const fields: SetupDraftField[] = [];
    for (const field of record.fields) {
      const key = record.fieldKey.get(field.id)!;
      const path = studioPath({ kind: 'field', record: recordKey, field: key });
      note(path, field.id);
      const why = out.get(field.id);
      if (why !== undefined) {
        kept.push({ key: path, name: field.label, why });
        continue;
      }
      fields.push(fieldOf(field, key, record, keyOf, byRecordId));
    }

    const keyFor = (fieldId: string | null): string | undefined => (fieldId === null ? undefined : (keyOf(schema.recordId, fieldId) ?? undefined));
    // The title as the workspace binds it; with none, the first field that can name a row; with none of those (edit only), headless.
    const titled = fields.find((field) => field.key === keyFor(boundId(schema, 'title')) && TITLE_TYPES.has(field.type));
    const headline = (titled ?? fields.find((field) => TITLE_TYPES.has(field.type)))?.key ?? '';
    const date = keyFor(boundId(schema, 'time_axis'));
    const measure = keyFor(boundId(schema, 'measure'));
    const sign = keyFor(boundId(schema, 'sign'));
    const focus = keyFor(boundId(schema, 'focused_input'));
    const tiles = record.kind === 'record' ? tilesOf(record, keyOf, kept, note) : [];

    const icon = schema.icon ?? null;
    // The record's own rules (F13), each by draft keys; one whose tree does not read back is left off.
    const rules = keyedRules(schema.rules ?? [], record, keyOf, byRecordId);
    const money = moneyOf(record, keyOf, input.money, headline);
    return {
      key: recordKey,
      name: schema.name,
      entryNoun: (schema.entryNoun?.one ?? '').trim() === '' ? 'entry' : schema.entryNoun!.one.trim(),
      kind: record.kind,
      direction: directionOf(schema, record.kind),
      fields,
      headlineFieldKey: headline,
      ...(record.kind === 'record' && date !== undefined ? { dateFieldKey: date } : {}),
      ...(record.kind === 'record' && measure !== undefined ? { measureFieldKey: measure } : {}),
      ...(record.kind === 'record' && sign !== undefined ? { signFieldKey: sign } : {}),
      ...(record.kind === 'record' && focus !== undefined ? { focusFieldKey: focus } : {}),
      ...(icon !== null && isRecordIconId(icon) ? { icon } : {}),
      ...(tiles.length === 0 ? {} : { tiles }),
      ...(rules.length === 0 ? {} : { rules }),
      ...(headline === '' ? { headless: true as const } : {}),
      ...money,
    };
  });

  /* roles: not the two every workspace is made with, never their people, no access (it is per person) */
  const made = input.roles.filter((role) => role.systemKey === null);
  const roleKeys = mintAll(made, (role) => role.name, 'role');
  const roles: StudioRole[] = [];
  made.forEach((role, at) => {
    const key = roleKeys[at]!;
    const path = studioPath({ kind: 'role', key });
    note(path, role.id);
    if (MADE_WITH.has(role.name.trim().toLowerCase()) || roles.length >= STUDIO_LIMITS.roles) {
      kept.push({ key: path, name: role.name, why: 'role' });
      return;
    }
    const purpose = role.purpose?.trim() ?? '';
    roles.push({ key, name: role.name.trim(), ...(purpose === '' ? {} : { purpose }), people: [], ...roleShapeOf(role), access: [] });
  });

  const draft: StudioDraft = {
    ...emptyStudioDraft({ currency: input.currency, language: input.language ?? DEFAULT_AGENT_LANGUAGE }),
    meta: { name: input.name, currency: input.currency, language: input.language ?? DEFAULT_AGENT_LANGUAGE },
    records: draftRecords,
    roles,
  };
  return { draft, built: mode === 'edit' ? built : {}, kept };
};

/* ------------------------------------------------------------------ money */

/**
 * A record's money as the draft holds it: its money rule by keys
 * (`specRuleOf`), a list's stock items mark by keys (Phase 9), and on the
 * Accounts list the mark with its Type field and its rows with their system
 * keys, since the rules name them: a copy seeds them, and Edit reads them so a
 * rule naming one is judged sound and a row it adds is told from the rows
 * that stand (step V2.2 of docs/plans/ledger.md). A rule that does not read
 * back (a field the draft does not hold) is left off, and the workspace keeps
 * it.
 */
const moneyOf = (
  record: RecordKeys,
  keyOf: (recordId: string, fieldId: string) => string | null,
  money: BuiltMoneyInput | undefined,
  headline: string,
): Pick<StudioRecord, 'posting' | 'accounts' | 'seedRows' | 'stockItems'> => {
  if (money === undefined) return {};
  const recordId = record.schema.recordId;
  const out: { posting?: StudioRecord['posting']; accounts?: StudioRecord['accounts']; seedRows?: StudioRecord['seedRows']; stockItems?: StudioRecord['stockItems'] } = {};
  // A list of stock items keeps its mark, by keys; one whose unit or pack field the draft does not hold is left off, and the workspace keeps it.
  const mark = money.stockItems?.get(recordId);
  if (mark !== undefined && record.kind === 'directory') {
    const unitFieldKey = keyOf(recordId, mark.unitFieldId);
    const packUnitFieldKey = mark.packUnitFieldId === null ? undefined : keyOf(recordId, mark.packUnitFieldId);
    const packSizeFieldKey = mark.packSizeFieldId === null ? undefined : keyOf(recordId, mark.packSizeFieldId);
    if (unitFieldKey !== null && packUnitFieldKey !== null && packSizeFieldKey !== null) {
      out.stockItems = {
        unitFieldKey,
        ...(packUnitFieldKey === undefined ? {} : { packUnitFieldKey }),
        ...(packSizeFieldKey === undefined ? {} : { packSizeFieldKey }),
      };
    }
  }
  const stored = money.rules.get(recordId);
  if (stored !== undefined) {
    const rowNames = new Map((money.accounts?.rows ?? []).map((row) => [row.accountId, row.name]));
    const rule = specRuleOf(stored.pattern, stored.map, {
      field: (fieldId) => keyOf(recordId, fieldId) ?? undefined,
      option: (fieldId, optionId) => record.optionKey.get(fieldId)?.get(optionId),
      row: (accountId) => rowNames.get(accountId),
    });
    // A list's charge plan (L23) is no rule a draft holds: the workspace keeps it, with the Charges record it writes into.
    if (rule !== null && record.kind === 'record') out.posting = rule;
  }
  const list = money.accounts;
  if (list !== null && list.recordId === recordId && record.kind === 'directory') {
    const typeFieldKey = keyOf(recordId, list.typeFieldId);
    if (typeFieldKey !== null) {
      out.accounts = { typeFieldKey };
      if (headline !== '' && list.rows.length > 0) {
        out.seedRows = list.rows.slice(0, STUDIO_LIMITS.seedRows).map((row) => {
          const account: SpecSeedAccount = {
            ...(row.systemKey === undefined || row.systemKey === null ? {} : { systemKey: row.systemKey }),
            ...(row.openItem === true ? { openItem: true } : {}),
            ...(row.contra === true ? { contra: true } : {}),
          };
          return {
            values: [
              { fieldKey: headline, value: row.name },
              { fieldKey: typeFieldKey, value: row.type },
            ],
            ...(Object.keys(account).length === 0 ? {} : { account }),
          };
        });
      }
    }
  }
  return out;
};

/* ------------------------------------------------------------------ fields */

const fieldOf = (
  field: RecordSchemaField,
  key: string,
  record: RecordKeys,
  keyOf: (recordId: string, fieldId: string) => string | null,
  byRecordId: ReadonlyMap<string, RecordKeys>,
): SetupDraftField => {
  const optionKeys = record.optionKey.get(field.id);
  const formula = field.type === 'formula' ? formulaOf(field.spec) : null;
  const keyed = formula === null ? null : keyedFormula(formula, record, keyOf, byRecordId);
  // A default or a show-when that does not read back is left off: the field still holds what it holds.
  const started = field.startsWith === null ? null : keyedFormula(field.startsWith, record, keyOf, byRecordId);
  // A select starts with one of its choices by the id the build minted (`rewriteDefault`); the draft says its key.
  const startsWith =
    started !== null && field.type === 'select' && started.expr.kind === 'const' && typeof started.expr.value === 'string'
      ? { ...started, expr: { kind: 'const' as const, value: optionKeys?.get(started.expr.value) ?? started.expr.value } }
      : started;
  const showWhen = (field.showWhen ?? null) === null ? null : keyedFormula(field.showWhen!, record, keyOf, byRecordId);
  const editableWhen =
    (field.editableWhen ?? null) === null ? null : keyedFormula(field.editableWhen!, record, keyOf, byRecordId);
  const mustBe = keyedRules(field.mustBe ?? [], record, keyOf, byRecordId);
  const target = field.targetRecordId === null ? undefined : byRecordId.get(field.targetRecordId);
  const filter = field.filter ?? null;
  const filterField = filter === null || target === undefined ? null : keyOf(target.schema.recordId, filter.field);
  const filterEquals = filter === null ? null : keyOf(record.schema.recordId, filter.equals);
  const help = field.helpText?.trim() ?? '';
  return {
    key,
    label: field.label,
    type: field.type as AuthorType,
    ...(field.required ? { required: true } : {}),
    ...(field.sticky ? { sticky: true } : {}),
    ...(help === '' ? {} : { help }),
    ...(field.type === 'select' && field.options !== null
      ? {
          options: field.options.map((option) => ({
            key: optionKeys?.get(option.id) ?? option.id,
            label: optionWord(option),
            ...(option.sign === null ? {} : { sign: option.sign }),
            ...(option.tone === null || option.tone === undefined ? {} : { tone: option.tone }),
          })),
        }
      : {}),
    ...(target === undefined ? {} : { targetRecordKey: target.recordKey }),
    ...(filterField !== null && filterEquals !== null ? { filter: { field: filterField, equals: filterEquals } } : {}),
    ...(field.type === 'multi_select' && typeof field.maxChosen === 'number' ? { maxChosen: field.maxChosen } : {}),
    ...(keyed === null ? {} : { formula: keyed }),
    ...(startsWith === null ? {} : { startsWith }),
    ...(startsWith !== null && field.startsOnce === true ? { startsOnce: true } : {}),
    ...(showWhen === null ? {} : { showWhen }),
    ...(editableWhen === null ? {} : { editableWhen }),
    ...(mustBe.length === 0 ? {} : { mustBe }),
  };
};

/* ------------------------------------------------------------------- cards */

/** A record's cards on top, from its first summary view; one that does not read back is kept. */
const tilesOf = (
  record: RecordKeys,
  keyOf: (recordId: string, fieldId: string) => string | null,
  kept: BuiltKept[],
  note: (path: string, id: string) => void,
): StudioTile[] => {
  const view = record.schema.views.find((one) => one.type === 'summary');
  const held = (view?.spec as { summaries?: unknown } | undefined)?.summaries;
  if (view === undefined || !Array.isArray(held)) return [];
  const summaries = held.map((tile): Record<string, unknown> => (typeof tile === 'object' && tile !== null ? (tile as Record<string, unknown>) : {}));
  const keys = mintAll(summaries, tileName, 'c');
  const own = (fieldId: unknown): string | null => (typeof fieldId === 'string' ? keyOf(record.schema.recordId, fieldId) : null);
  const tiles: StudioTile[] = [];
  summaries.forEach((tile, at) => {
    const key = keys[at]!;
    const path = studioPath({ kind: 'card', record: record.recordKey, card: key });
    note(path, `${view.id}#${at}`);
    const name = tileName(tile);
    const said = tileOf(tile, name, record, own);
    if (said === null || tiles.length >= STUDIO_LIMITS.tilesPerRecord) kept.push({ key: path, name, why: 'card' });
    else tiles.push({ key, ...said });
  });
  return tiles;
};

const tileOf = (tile: Record<string, unknown>, name: string, record: RecordKeys, own: (fieldId: unknown) => string | null): Omit<StudioTile, 'key'> | null => {
  const expr = typeof tile.expr === 'string' ? TILE_EXPR.exec(tile.expr) : null;
  const agg = expr?.[1];
  const fieldKey = own(expr?.[2]);
  // A tile that names no card is drawn as a stat (insight-layout.ts), so it reads back as one.
  const card = tile.card === undefined ? 'insight.stat' : tile.card;
  if (name === '' || agg === undefined || !(SETUP_AGGS as readonly string[]).includes(agg) || fieldKey === null || !isInsightCardId(card)) return null;
  const scope = tile.scope;
  if (scope !== undefined && !(SETUP_TILE_SCOPES as readonly unknown[]).includes(scope)) return null;
  const groupBy = tile.group_by === undefined ? undefined : own(tile.group_by);
  if (groupBy === null) return null;
  const where: SetupDraftTileCondition[] = [];
  for (const raw of Array.isArray(tile.where) ? (tile.where as unknown[]) : []) {
    const condition = (typeof raw === 'object' && raw !== null ? raw : {}) as { fieldId?: unknown; op?: unknown; value?: unknown };
    const key = own(condition.fieldId);
    if (key === null || !(SETUP_TILE_OPS as readonly unknown[]).includes(condition.op)) return null;
    const op = condition.op as SetupDraftTileCondition['op'];
    const field = record.fields.find((one) => one.id === condition.fieldId);
    if (condition.value === undefined) where.push({ fieldKey: key, op });
    else if (typeof condition.value === 'number') where.push({ fieldKey: key, op, num: condition.value });
    else {
      const optionKey = typeof condition.value === 'string' && field !== undefined ? record.optionKey.get(field.id)?.get(condition.value) : undefined;
      if (optionKey === undefined) return null;
      where.push({ fieldKey: key, op, optionKey });
    }
  }
  return {
    label: name,
    card,
    agg: agg as StudioTile['agg'],
    fieldKey,
    ...(scope === undefined ? {} : { scope: scope as StudioTile['scope'] }),
    ...(groupBy === undefined ? {} : { groupByFieldKey: groupBy }),
    ...(typeof tile.days === 'number' ? { days: tile.days } : {}),
    ...(typeof tile.limit === 'number' ? { limit: tile.limit } : {}),
    ...(where.length === 0 ? {} : { where }),
  };
};
